import {
  React,
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef
} from 'react'
import { Checkbox, Divider, Grid } from '@mui/material'
import { FormControlLabel, FormGroup } from '@mui/material'
import { TextBold, TextRegular, TextSemiBold } from '../TextStyling'
import { useTheme } from '@mui/styles'

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import AppButton from '../AppButton'
import NoRecordFound from '../NoRecordFound'

const FilterJobsResponsive = forwardRef(({ dropDown, onFilterChange }, ref) => {
  const theme = useTheme()

  // Initialize state with values from sessionStorage, if any
  const getStoredState = (key, defaultState) => {
    const storedState = sessionStorage.getItem(key)
    return storedState ? JSON.parse(storedState) : defaultState
  }

  const [open, setOpen] = useState(
    getStoredState('openState', {
      jobType: false,
      workLocation: false,
      jobFunction: false,
      industry: false,
      location: false,
      referralReward: false
    })
  )
  const [checkedJobType, setCheckedJobType] = useState(
    getStoredState('checkedJobType', [])
  )
  const [checkedWorkLocation, setCheckedWorkLocation] = useState(
    getStoredState('checkedWorkLocation', [])
  )
  const [checkedJobFunction, setCheckedJobFunction] = useState(
    getStoredState('checkedJobFunction', [])
  )
  const [checkedIndustry, setCheckedIndustry] = useState(
    getStoredState('checkedIndustry', [])
  )
  const [checkedReferralReward, setCheckedReferralReward] = useState(
    getStoredState('checkedReferralReward', [])
  )

  const isInitialMount = useRef(true) // Ref to track initial render

  const handleCheckBoxChange = (category, item) => {
    let data
    switch (category) {
      case 'jobType':
        data = updateCheckedArray(checkedJobType, item)
        setCheckedJobType(data)
        break
      case 'workLocation':
        data = updateCheckedArray(checkedWorkLocation, item)
        setCheckedWorkLocation(data)
        break
      case 'industry':
        data = updateCheckedArray(checkedIndustry, item)
        setCheckedIndustry(data)
        break
      case 'jobFunction':
        data = updateCheckedArray(checkedJobFunction, item)
        setCheckedJobFunction(data)
        break
      case 'referralReward':
        data = updateCheckedArray(checkedReferralReward, item)
        setCheckedReferralReward(data)
        break
      default:
        break
    }
  }

  const updateCheckedArray = (array, item) => {
    const index = array.findIndex(e => e === item.id)
    const newArray = [...array]
    if (index === -1) {
      newArray.push(item.id)
    } else {
      newArray.splice(index, 1)
    }
    return newArray
  }

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
    } else {
      // Save filter states to sessionStorage
      sessionStorage.setItem('checkedJobType', JSON.stringify(checkedJobType))
      sessionStorage.setItem(
        'checkedWorkLocation',
        JSON.stringify(checkedWorkLocation)
      )
      sessionStorage.setItem(
        'checkedJobFunction',
        JSON.stringify(checkedJobFunction)
      )
      sessionStorage.setItem('checkedIndustry', JSON.stringify(checkedIndustry))
      sessionStorage.setItem(
        'checkedReferralReward',
        JSON.stringify(checkedReferralReward)
      )

      sessionStorage.setItem('openState', JSON.stringify(open))

      onFilterChange({
        jobTypes: checkedJobType.length > 0 ? checkedJobType : null,
        workLocationType:
          checkedWorkLocation.length > 0 ? checkedWorkLocation : null,
        jobFunction: checkedJobFunction.length > 0 ? checkedJobFunction : null,
        jobIndustry: checkedIndustry.length > 0 ? checkedIndustry : null,
        referralReward:
          checkedReferralReward.length > 0 ? checkedReferralReward : null
      })
    }
  }, [
    checkedJobType,
    checkedJobFunction,
    checkedIndustry,
    checkedWorkLocation,
    checkedReferralReward,
    open
  ])

  const handleOpen = field => {
    setOpen(prevState => {
      const newState = { ...prevState }
      if (newState[field]) {
        newState[field] = false
      } else {
        Object.keys(newState).forEach(key => {
          newState[key] = false
        })
        newState[field] = true
      }

      return newState
    })
  }

  return (
    <Grid
      container
      style={{
        height: 'min-content',
        maxHeight: '100%',
        backgroundColor: theme.palette.background.paper
      }}
    >
      {/* Filter Section */}
      <Grid
        item
        xs={12}
        style={{
          display: 'flex',
          flexDirection: 'column',
          marginTop: '3rem'
        }}
      >
        {/* Clear All Button */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px'
          }}
        >
          <TextBold style={{ fontSize: '16px' }}>All Filters</TextBold>

          <TextRegular
            onClick={() => {
              setCheckedJobType([])
              setCheckedWorkLocation([])
              setCheckedJobFunction([])
              setCheckedIndustry([])
              setCheckedReferralReward([])
              setOpen({
                jobType: false,
                workLocation: false,
                jobFunction: false,
                industry: false,
                location: false,
                referralReward: false
              })
              sessionStorage.clear() // Clear session storage on reset
            }}
            style={{ color: theme.palette.text.disabled, cursor: 'pointer' }}
          >
            Clear All
          </TextRegular>
        </div>

        <Divider />
      </Grid>

      {/* Filter categories */}
      <Grid item xs={4.5}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px',
            background: !open.jobType && theme.palette.background.default
          }}
          onClick={() => handleOpen('jobType')}
        >
          <TextSemiBold style={{ fontSize: '16px', lineHeight: '24px' }}>
            Job Type
          </TextSemiBold>
        </div>
        <Divider />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px',
            background: !open.workLocation && theme.palette.background.default
          }}
          onClick={() => handleOpen('workLocation')}
        >
          <TextSemiBold style={{ fontSize: '16px', lineHeight: '24px' }}>
            Work Location
          </TextSemiBold>
        </div>
        <Divider />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px',
            background: !open.jobFunction && theme.palette.background.default
          }}
          onClick={() => handleOpen('jobFunction')}
        >
          <TextSemiBold style={{ fontSize: '16px', lineHeight: '24px' }}>
            Job Function
          </TextSemiBold>
        </div>
        <Divider />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px',
            background: !open.industry && theme.palette.background.default
          }}
          onClick={() => handleOpen('industry')}
        >
          <TextSemiBold style={{ fontSize: '16px', lineHeight: '24px' }}>
            Industry
          </TextSemiBold>
        </div>
        <Divider />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px',
            background: !open.referralReward && theme.palette.background.default
          }}
          onClick={() => handleOpen('referralReward')}
        >
          <TextSemiBold style={{ fontSize: '16px', lineHeight: '24px' }}>
            Referral Reward
          </TextSemiBold>
        </div>
        <Divider />
      </Grid>

      {/* Filter values section */}
      <Grid item xs={0.2} style={{ display: 'flex' }}>
        <Divider orientation='vertical' />
      </Grid>
      <Grid item xs={7.3} style={{ overflow: 'auto' }}>
        {open.jobType && (
          <div style={{ padding: '0px 16px' }}>
            <FormGroup>
              {dropDown?.jobTypes?.map(item => (
                <FormControlLabel
                  key={item.id}
                  label={<span style={{ fontSize: '14px' }}>{item.name}</span>}
                  control={
                    <Checkbox
                      name={item.id}
                      checked={checkedJobType.includes(item.id)}
                      onChange={() => handleCheckBoxChange('jobType', item)}
                    />
                  }
                />
              ))}
            </FormGroup>
          </div>
        )}
        {open.workLocation && (
          <div style={{ padding: '0px 16px' }}>
            <FormGroup>
              {dropDown?.workLocationTypes?.map(item => (
                <FormControlLabel
                  key={item.id}
                  label={<span style={{ fontSize: '14px' }}>{item.name}</span>}
                  control={
                    <Checkbox
                      name={item.id}
                      checked={checkedWorkLocation.includes(item.id)}
                      onChange={() =>
                        handleCheckBoxChange('workLocation', item)
                      }
                    />
                  }
                />
              ))}
            </FormGroup>
          </div>
        )}
        {open.jobFunction && (
          <div
            style={{ padding: '0px 16px', overflow: 'auto', height: '84vh' }}
          >
            <FormGroup>
              {dropDown?.jobFunctions?.map(item => (
                <FormControlLabel
                  key={item.id}
                  label={<span style={{ fontSize: '14px' }}>{item.name}</span>}
                  control={
                    <Checkbox
                      name={item.id}
                      checked={checkedJobFunction.includes(item.id)}
                      onChange={() => handleCheckBoxChange('jobFunction', item)}
                    />
                  }
                />
              ))}
            </FormGroup>
          </div>
        )}
        {open.industry && (
          <div
            style={{ padding: '0px 16px', overflow: 'auto', height: '84vh' }}
          >
            <FormGroup>
              {dropDown?.industries?.map(item => (
                <FormControlLabel
                  key={item.id}
                  label={<span style={{ fontSize: '14px' }}>{item.name}</span>}
                  control={
                    <Checkbox
                      name={item.id}
                      checked={checkedIndustry.includes(item.id)}
                      onChange={() => handleCheckBoxChange('industry', item)}
                    />
                  }
                />
              ))}
            </FormGroup>
          </div>
        )}
        {open.referralReward && (
          <div style={{ padding: '0px 16px' }}>
            <FormGroup>
              {[
                { id: 'fixed', name: 'Fixed' },
                { id: 'percentage', name: 'Percentage' },
                { id: 'hourly', name: 'Hourly' }
              ].map(item => (
                <FormControlLabel
                  key={item.id}
                  label={<span style={{ fontSize: '14px' }}>{item.name}</span>}
                  control={
                    <Checkbox
                      name={item}
                      checked={checkedReferralReward.includes(item.id)}
                      onChange={() =>
                        handleCheckBoxChange('referralReward', item)
                      }
                    />
                  }
                />
              ))}
            </FormGroup>
          </div>
        )}

        {!open.jobType &&
          !open.workLocation &&
          !open.jobFunction &&
          !open.industry &&
          !open.referralReward && (
            <NoRecordFound
              title={'No filter chosen'}
              style={{ width: '100%' }}
            />
          )}
      </Grid>
    </Grid>
  )
})

export default FilterJobsResponsive
